import { red, grey } from '@mui/material/colors';
import { createTheme, responsiveFontSizes } from '@mui/material/styles';
import Fonts from '../assets/fonts';

const fontFamilyRoboto = {
  fontFamily: [
    'Roboto',
    'Arial',
    'sans-serif',
    '"Apple Color Emoji"',
    '"Segoe UI Emoji"',
    '"Segoe UI Symbol"',
  ].join(','),
};

const fontFamilyMetropolis = {
  fontFamily: [
    'Metropolis',
    'Arial',
    'sans-serif',
    '"Apple Color Emoji"',
    '"Segoe UI Emoji"',
    '"Segoe UI Symbol"',
  ].join(','),
  letterSpacing: '0.015rem',
};

// Centralized Burgundy palette to fix the previous hardcoded scattering
const BURGUNDY = {
  main: '#960025ff',
  light: '#f7b9caff', 
  lighter: '#fadfe7ff', 
  dark: '#4d0013', 
  activeLight: '#af1c43ff', 
  alpha10: 'rgba(128, 0, 32, 0.1)',
  alpha70: 'rgba(128, 0, 32, 0.7)',
  alpha72: 'rgba(128, 0, 32, 0.72)',
  // Dark mode specific adjustments for visibility against dark backgrounds
  darkMain: '#b32446', 
  darkLighter: '#c95973', 
  darkDark: '#590016', 
};

// A custom theme for this app
const lightMuiTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#FFF',
      another: '#efe4deff',
      contrastText: '#4e4e4eff',
    },
    secondary: {
      main: BURGUNDY.main,
      light: BURGUNDY.light,
      lighter: BURGUNDY.lighter,
      dark: BURGUNDY.dark,
      activeLight: BURGUNDY.activeLight,
    },
    error: {
      main: red.A400,
    },
    background: {
      default: '#FFF',
      paper: '#F1F3F4',
      highlight: '#F1F3F4',
    },
    text: {
      primary: '#000000',
      secondary: grey[700],
      link: '#575757ff',
    },
  },
  typography: {
    ...fontFamilyRoboto,
    overline: {
      fontWeight: 500,
      fontSize: '0.7rem',
    },
    sectionHeader: {
      ...fontFamilyMetropolis,
      fontSize: '1.1rem',
      fontWeight: 600,
    },
    sectionItem: {
      ...fontFamilyMetropolis,
      fontSize: '0.87rem',
      fontWeight: 400,
    },
  },
  shape: {
    borderRadius: '0.5rem',
  },
  zIndex: {
    appBar: 1200,
    drawer: 1100,
  },
  mixins: {
    drawer: {
      minWidth: 280,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        ${Object.values(Fonts).map(font => `
          @font-face {
            font-family: '${font.fontFamily}';
            font-style: ${font.fontStyle};
            font-display: ${font.fontDisplay};
            font-weight: ${font.fontWeight};
            src: ${font.src};
          }
        `).join('\n')}
      `,
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          ...fontFamilyMetropolis,
          fontWeight: 500,
          fontSize: '0.87rem',
        },
      },
    },
  },
  custom: {
    fontFamily: {
      roboto: fontFamilyRoboto,
      metropolis: fontFamilyMetropolis,
    },
    palette: {
      iconColor: '#141b2b',
      iconColorSecondary: BURGUNDY.main,
      itemBorderColor: '#DDDDDD',
      iconHighlight: grey[900],
      notesCheckbox: grey[700],
      profilePopColor: '#FFF',
      navDrawer: {
        activeItem: BURGUNDY.activeLight,
        linkHover: BURGUNDY.main,
      },
      noteBackground: {
        default: '#0000',
        red: '#F28B82',
        orange: '#FBBC04',
        yellow: '#FFF475',
        green: '#CCFF90',
        cyan: '#A7FFEB',
        lightblue: '#CBF0F8',
        darkblue: '#AECBFA',
        purple: '#D7AEFB',
        pink: '#FDCFE8',
        brown: '#E6C9A8',
        grey: '#E8EAED',
      },
      noteColorCheck: '#0007',
      labelBackground: '#0002',
    },
  },
});

const darkMuiTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#2D2E30',
      another: '#2D2E30',
      contrastText: '#E8EAED',
    },
    secondary: {
      main: BURGUNDY.darkMain,
      light: BURGUNDY.alpha10,
      lighter: BURGUNDY.darkLighter,
      dark: BURGUNDY.darkDark,
      activeLight: BURGUNDY.darkMain,
    },
    error: {
      main: red.A400,
    },
    background: {
      default: '#202124',
      paper: '#2D2E30',
      highlight: '#47484a78',
    },
    text: {
      primary: '#E8EAED',
      secondary: '#FFFFFFDE',
      link: '#90CAF9',
    },
  },
  typography: {
    ...fontFamilyRoboto,
    overline: {
      fontWeight: 500,
      fontSize: '0.7rem',
    },
    sectionHeader: {
      ...fontFamilyMetropolis,
      fontSize: '1.1rem',
      fontWeight: 600,
    },
    sectionItem: {
      ...fontFamilyMetropolis,
      fontSize: '0.87rem',
      fontWeight: 400,
    },
  },
  shape: {
    borderRadius: '0.5rem',
  },
  zIndex: {
    appBar: 1200,
    drawer: 1100,
  },
  mixins: {
    drawer: {
      minWidth: 280,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        ${Object.values(Fonts).map(font => `
          @font-face {
            font-family: '${font.fontFamily}';
            font-style: ${font.fontStyle};
            font-display: ${font.fontDisplay};
            font-weight: ${font.fontWeight};
            src: ${font.src};
          }
        `).join('\n')}
      `,
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          ...fontFamilyMetropolis,
          fontWeight: 500,
          fontSize: '0.87rem',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        contained: {
          '&.MuiButton-containedSecondary': {
            // Changed from #000000 to white to remain legible against dark burgundy
            color: '#FFFFFF', 
          },
        },
        outlined: {
          '&.MuiButton-outlinedSecondary': {
            color: BURGUNDY.darkMain,
            borderColor: BURGUNDY.darkMain,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#2D2E30',
          '&:hover': {
            backgroundColor: '#2D2E30',
          },
        },
      },
    },
  },
  custom: {
    fontFamily: {
      roboto: fontFamilyRoboto,
      metropolis: fontFamilyMetropolis,
    },
    palette: {
      iconColor: '#FFFFFF',
      iconColorSecondary: BURGUNDY.darkMain,
      itemBorderColor: '#5F6368',
      iconHighlight: '#888A8B',
      notesCheckbox: '#5F6368',
      profilePopColor: '#2D2E30',
      navDrawer: {
        activeItem: BURGUNDY.alpha72,
        linkHover: BURGUNDY.darkMain,
      },
      card: {
        hoverBackground: BURGUNDY.alpha70,
      },
      noteBackground: {
        default: '#0000',
        red: '#5C2B29',
        orange: '#614A19',
        yellow: '#635D18',
        green: '#345920',
        cyan: '#16504B',
        lightblue: '#2D555E',
        darkblue: '#1E3A5F',
        purple: '#42275E',
        pink: '#5B2245',
        brown: '#442F19',
        grey: '#3C3F43',
      },
      noteColorCheck: '#FFF7',
      labelBackground: '#0002',
    },
  },
});

export const light = responsiveFontSizes(lightMuiTheme);
export const dark = responsiveFontSizes(darkMuiTheme);
